import React, { Fragment, useCallback, useEffect, useState } from 'react'

import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    useNodesState,
    useEdgesState,
    MarkerType,
    Controls,
    useReactFlow,
    MiniMap,
    Position,
    Panel,
    Connection,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import FloatingEdge, { FloatingEdgeType } from './CustomLayout/FloatingEdge'
import FloatingConnectionLine from './FloatingFunction/FloatingConnectionLine'
import { useNavigate, useParams, useLocation } from 'react-router'
import Api from 'axiosApi/api'
import { CreateCommand, UpdateCommand, WorkflowDetails, WorkflowDetailsResponse, WorkflowStateDTO } from 'axiosApi/models'
import { useApi } from '../../api/ApiProvider'
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider'
import intl from 'components/i18n/ReactIntlWrapper'
import { Box, Button, Card, Chip, Divider, FormControl, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import EditableNode, { EditableNodeType } from './CustomLayout/EditableNode'
import EditableNodeDrawer from './CustomLayout/EditableNodeDrawer'
import FloatingEdgeDrawer from './CustomLayout/FloatingEdgeDrawer'
import { AutomaticActionToolbox, ParameterConfiguration } from './AutomaticActions';
import { STATE_TYPE_TO_ID, ID_TO_STATE_TYPE, AutomaticStateType } from './AutomaticActions/types';
import MuiIconDisplay from 'components/MuiIconPicker/MuiIconDisplay';

import { WorkflowTransitionDTO } from 'axiosApi/models/workflow-transition-dto';
import { AxiosResponse } from 'axios';
import { confirmAlert } from "react-confirm-alert";
import { handleAPIError } from 'common/errorHandler';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES } from 'common/permissions';
import { useLayout } from 'components/Layout/LayoutProvider';
import { ArrowBack, Delete, Edit, FlipToFront, MultipleStop, SwipeRightAlt, TurnSharpRight, Close, Settings as AutomaticActionsIcon } from '@mui/icons-material';
import AddStateModal from './AddStateModal';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Select, InputLabel } from '@mui/material';
import { WORKFLOW_MODULES } from 'common/constants';

const initialNodes: EditableNodeType[] = []
const initialEdges: FloatingEdgeType[] = []

const edgeTypes = {
    floating: FloatingEdge as React.FC<any>,
}

const defaultViewport = { x: 10, y: 15, zoom: 3 }

const WorkflowEditor: React.FC = () => {
    const { id } = useParams()
    const api: Api = useApi()
    const toast = useToastMessageQueue()
    const [workflowData, setWorkflowData] = useState<UpdateCommand>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ type: 'node' | 'edge', id: string } | null>(null);
    
    
    const [nodes, setNodes, onNodesChange] = useNodesState<EditableNodeType>(initialNodes)
    const [edges, setEdges, onEdgesChange] = useEdgesState<FloatingEdgeType>(initialEdges)

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [addTransitionMode, setAddTransitionMode] = useState<boolean>(false);
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const { fitView, screenToFlowPosition } = useReactFlow();

    const navigate = useNavigate();
    const location = useLocation();
    const [showNavigationDialog, setShowNavigationDialog] = useState(false);
    const [nextLocation, setNextLocation] = useState<string | null>(null);

    // Intercept all link clicks globally
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!hasChanges) return;

            // Find if click was on a link or inside a link
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            
            if (link && link.href) {
                const linkUrl = new URL(link.href);
                const currentUrl = new URL(window.location.href);
                
                // Check if it's an internal navigation (same origin)
                if (linkUrl.origin === currentUrl.origin && linkUrl.pathname !== currentUrl.pathname) {
                    e.preventDefault();
                    e.stopPropagation();
                    setNextLocation(linkUrl.pathname + linkUrl.search + linkUrl.hash);
                    setShowNavigationDialog(true);
                }
            }
        };

        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [hasChanges]);

    // Handle browser back/forward buttons
    useEffect(() => {
        if (!hasChanges) return;

        // Push a dummy state to detect back button
        window.history.pushState(null, '', window.location.href);

        const handlePopState = (e: PopStateEvent) => {
            if (hasChanges) {
                // Push state again to prevent navigation
                window.history.pushState(null, '', window.location.href);
                // Show confirmation dialog
                setShowNavigationDialog(true);
                // Store the fact that user wants to go back
                setNextLocation('back');
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [hasChanges]);

    // Handle browser refresh/close
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasChanges]);

    // Custom navigate function that checks for unsaved changes
    const navigateWithConfirmation = (path: string) => {
        if (hasChanges) {
            setNextLocation(path);
            setShowNavigationDialog(true);
        } else {
            navigate(path);
        }
    };

    const handleNavigationCancel = () => {
        setShowNavigationDialog(false);
        setNextLocation(null);
    };

    const handleNavigationConfirm = () => {
        setShowNavigationDialog(false);
        setHasChanges(false);
        if (nextLocation) {
            if (nextLocation === 'back') {
                // Use navigate(-1) for back navigation
                setTimeout(() => navigate(-1), 0);
            } else {
                navigate(nextLocation);
            }
            setNextLocation(null);
        }
    };

    const createWorkflow = (workflowData: CreateCommand) => {
        const dataReq = clearIdsOfNewStatesAndTransitions(workflowData);
        setLoading(true);
        return api.workflowApi.apiVversionWorkflowPost('1', dataReq, {}).then((response) => {
            if(response.status === 200){
                const { data } = response.data;
                const workflowData = loadWorkflowData(data);
                //handleLoadJson(data)
                handleLoadWorkflowData(workflowData);
                setHasChanges(false);
                toast.success({ body: intl.get('workflowEditor.messages.created') });
                navigate(`/workflow/${data.id}`,{
                    replace: true
                })
                return response;
            }else{
                throw new Error('Error saving workflow')
            }
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false)
        })
    }

    const saveWorkflow = (workflowData: UpdateCommand) => {
        const dataReq = clearIdsOfNewStatesAndTransitions(workflowData);
        setLoading(true);
        return api.workflowApi.apiVversionWorkflowPut('1', dataReq, {}).then((response) => {
            if(response.status === 200){
                const { data } = response.data;
                const workflowData = loadWorkflowData(data);
                
                handleLoadWorkflowData(workflowData);
                setHasChanges(false);
                toast.success({ body: intl.get('workflowEditor.messages.updated') });
                
                return response;
            }else{
                throw new Error('Error saving workflow')
            }
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false)
        })
    }

    const validateForm = () => {
        //validate workflow name
        if(!workflowData.name){
            toast.warning({ header: intl.get('error'), body: intl.get('workflow.name.required') });
            return false;
        }
        return true;
    }

    const saveAction = (actionData: any) => {
        if(!validateForm()){
            return;
        }
        if(actionData?.id){
            saveWorkflow(actionData);
        }else{
            createWorkflow(actionData);
        }
    }

    const saveWorkflowState = (workflowData: UpdateCommand) => {
        setWorkflowData(workflowData);
        setHasChanges(true);
        return new Promise((resolve, reject) => {
            resolve('ok');
        })
    }

    const fetchWorkflowDetail = async (idNumber: number) => {
        setLoading(true)
        api.workflowApi
            .apiVversionWorkflowIdGet(idNumber, '1', {})
            .then((response: AxiosResponse<WorkflowDetailsResponse, any>) => {
                if (response.data) {
                    const { data } = response.data;
                    const workflowData = loadWorkflowData(data);
                    
                    handleLoadWorkflowData(workflowData)
                }
            })
            .catch((error)=>{
                handleAPIError(error, toast, errors);    
                setErrors({ ...errors });            
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        if (id != 'new') {
            fetchWorkflowDetail(parseInt(id))
        }
        else {
            handleLoadWorkflowData(initializeWorkflow());
        }
    }, [id])

    const initializeWorkflow = (): UpdateCommand => {
        const workflowData: UpdateCommand = {
            name: "",
            id: 0,
            idModule: 1,
            states: [],
            transitions: []
        }

        return workflowData;
    }

    const loadWorkflowData = (workflowDetail: WorkflowDetails): UpdateCommand => {
        const workflowData: UpdateCommand = {
            name: workflowDetail.name,
            id: workflowDetail.id,
            idModule: workflowDetail.idModule,
            states: workflowDetail.states.map((state) => ({
                id: state.id,
                isDeleted: state.isDeleted,
                name: state.name,
                idWorkflow: state.idWorkflow,
                complete: state.complete,
                extraAttributes: state.extraAttributes,
                labelKey: state.labelKey,
                action: state.action,
                reactFlowId: state.reactFlowId,
                viewScopes: state.viewScopes,
                editScopes: state.editScopes,
                deleteScopes: state.deleteScopes,
                initial: state.initial,
                color: state.displayColor,
                editable: state.editable,
                deletable: state.deletable,
                viewable: state.viewable,
                editableByOwner: state.editableByOwner,
                deletableByOwner: state.deletableByOwner,
                viewableByOwner: state.viewableByOwner,
                displayColor: state.displayColor,
                idStateType: state.idStateType,
                idWorkflowStateSuccess: state.idWorkflowStateSuccess,
                idWorkflowStateFailure: state.idWorkflowStateFailure,
                parameterConfiguration: state.parameterConfiguration,
            })),
            transitions: workflowDetail.transitions.map((transition) => ({
                id: transition.id,
                isDeleted: transition.isDeleted,
                idWorkflowStateFrom: transition.idWorkflowStateFrom,
                idWorkflowStateTo: transition.idWorkflowStateTo,
                actionName: transition.actionName,
                labelKey: transition.labelKey,
                displayOrder: transition.displayOrder,
                displayColor: transition.displayColor,
                icon: transition.icon,
                allowComments: transition.allowComments,
                extraAttributes: transition.extraAttributes,
                idScope: transition.idScope,
                reactFlowId: transition.id.toString(),
                stateFromReactFlowId: transition.idWorkflowStateFrom? transition.idWorkflowStateFrom.toString() : null,
                stateToReactFlowId: transition.idWorkflowStateTo? transition.idWorkflowStateTo.toString() : null,
            }))
        }

        return workflowData;
    }

    const handleLoadWorkflowData = (workflowData: UpdateCommand) => {
        setWorkflowData(workflowData);
        const center = { x: 0, y: 0 };
        const gap = {
            x: 300,
            y: 300,
        }

        console.log('WorkflowData states (checking all properties):', workflowData.states.map(s => ({
            id: s.id,
            name: s.name,
            idStateType: s.idStateType,
            IdStateType: (s as any).IdStateType,
            idWorkflowStateSuccess: s.idWorkflowStateSuccess,
            IdWorkflowStateSuccess: (s as any).IdWorkflowStateSuccess,
            idWorkflowStateFailure: s.idWorkflowStateFailure,
            IdWorkflowStateFailure: (s as any).IdWorkflowStateFailure,
            parameterConfiguration: s.parameterConfiguration,
            ParameterConfiguration: (s as any).ParameterConfiguration,
            allKeys: Object.keys(s)
        })));

        const loadedNodes: EditableNodeType[] = workflowData.states.map((state) => {
            const degrees = state.id * (360 / (workflowData.states.length-1));
            const radians = degrees * (Math.PI / 180);
            const extraAttributes = JSON.parse(state.extraAttributes ?? '{}')
            const x = extraAttributes?.position?.x || gap.x * Math.cos(radians) + center.x;
            const y = extraAttributes?.position?.y || gap.y * Math.sin(radians) + center.y;

            // Reconstruct automatic action data if this is an automatic action node
            let automaticAction = undefined;
            if (state.idStateType) {
                const stateTypeString = ID_TO_STATE_TYPE[state.idStateType as number];
                console.log('Loading automatic action state:', {
                    stateId: state.id,
                    idStateType: state.idStateType,
                    stateTypeString,
                    parameterConfiguration: state.parameterConfiguration
                });
                automaticAction = {
                    stateType: stateTypeString || state.idStateType,
                    config: state.parameterConfiguration ? JSON.parse(state.parameterConfiguration) : { parameters: [], retryConfig: { maxRetries: 3, retryDelay: 1000 } }
                };
                // Mark the node as automatic in extraAttributes
                extraAttributes.automatic = true;
            }

            return ({
                id: state.reactFlowId || state.id.toString(),
                type: 'editableNode',
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                data: {
                    internalId: `${state.id}`,
                    name: state.name,
                    labelKey: state.labelKey,
                    editScopes: state.editScopes,
                    viewScopes: state.viewScopes,
                    deleteScopes: state.deleteScopes,
                    initial: state.initial,
                    final: state.complete,
                    transitionEditMode: false,
                    color: state.displayColor,
                    automaticAction,
                    extraAttributes: {
                        ...extraAttributes,
                    },
                },
                position: {
                    x: x,
                    y: y,
                },
            })
        })

        const loadedEdges: FloatingEdgeType[] = workflowData.transitions.map((transition) => {
            const extraAttributes = JSON.parse(transition.extraAttributes ?? '{}')
            return {
                id: transition.id.toString(),
                source: transition.stateFromReactFlowId,
                target: transition.stateToReactFlowId,
                type: 'floating',
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 30,
                    height: 30,
                    color: '#black',
                },
                data: {
                    actionName: transition.actionName,
                    labelKey: transition.labelKey,
                    internalId: transition.id.toString(),
                    scope: transition.idScope,
                    AllowComments: transition.allowComments,
                    Icon: transition.icon,
                    DisplayColor: transition.displayColor,
                    extraAttributes: {
                        ...extraAttributes,
                    }
                },
            }
        })

        // Create edges for automatic action nodes from idWorkflowStateSuccess and idWorkflowStateFailure
        workflowData.states.forEach((state) => {
            if (state.idStateType) {
                const sourceNodeId = state.reactFlowId || state.id.toString();
                
                // Create success edge if it exists
                if (state.idWorkflowStateSuccess) {
                    const targetState = workflowData.states.find(s => s.id === state.idWorkflowStateSuccess);
                    if (targetState) {
                        const targetNodeId = targetState.reactFlowId || targetState.id.toString();
                        loadedEdges.push({
                            id: `auto-success-${state.id}`,
                            source: sourceNodeId,
                            target: targetNodeId,
                            sourceHandle: 'success',
                            type: 'floating',
                            markerEnd: {
                                type: MarkerType.ArrowClosed,
                                width: 30,
                                height: 30,
                                color: '#4caf50',
                            },
                            data: {
                                actionName: 'Success',
                                labelKey: 'Success',
                                scope: 0,
                                AllowComments: false,
                                Icon: 'mdi:check-circle',
                                DisplayColor: '#4caf50',
                                transitionType: 'success' as 'success' | 'error',
                                extraAttributes: {},
                            },
                        });
                    }
                }
                
                // Create error edge if it exists
                if (state.idWorkflowStateFailure) {
                    const targetState = workflowData.states.find(s => s.id === state.idWorkflowStateFailure);
                    if (targetState) {
                        const targetNodeId = targetState.reactFlowId || targetState.id.toString();
                        loadedEdges.push({
                            id: `auto-error-${state.id}`,
                            source: sourceNodeId,
                            target: targetNodeId,
                            sourceHandle: 'error',
                            type: 'floating',
                            markerEnd: {
                                type: MarkerType.ArrowClosed,
                                width: 30,
                                height: 30,
                                color: '#f44336',
                            },
                            data: {
                                actionName: 'Error',
                                labelKey: 'Error',
                                scope: 0,
                                AllowComments: false,
                                Icon: 'mdi:alert-circle',
                                DisplayColor: '#f44336',
                                transitionType: 'error' as 'success' | 'error',
                                extraAttributes: {},
                            },
                        });
                    }
                }
            }
        });

        //TODO

        pushVirtualInitialNode(loadedNodes, loadedEdges);
        pushVirtualFinalNode(loadedNodes, loadedEdges);

        console.log('About to set nodes:', loadedNodes.map(n => ({
            id: n.id,
            hasAutomaticAction: !!n.data?.automaticAction,
            automaticAction: n.data?.automaticAction
        })));

        setNodes(loadedNodes);
        setEdges(loadedEdges);
        fitView();
    }

    const nodeTypes = {
        editableNode: EditableNode as React.FC<any>,
    }

    const [nodeSelected, setNodeSelected] = useState<EditableNodeType>(null);
    const [edgeSelected, setEdgeSelected] = useState<FloatingEdgeType | Connection>(null);

    const [newStatesUuid, setNewStatesUuid] = useState<string[]>([]);
    const [newTransitionsUuid, setNewTransitionsUuid] = useState<string[]>([]);

    const isNewTransition = (id: string) => newTransitionsUuid.includes(id);
    const isNewState = (id: string) => newStatesUuid.includes(id);

    const onConnect = useCallback(
        (params: Connection) => {
          console.log('onConnect called with params:', params);
          // For automatic action handles (success/error), create the edge immediately
          if (params.sourceHandle === 'success' || params.sourceHandle === 'error') {
            console.log('Creating automatic action edge');
            const newUuid = createRandomId();
            const newEdge: FloatingEdgeType = {
              id: newUuid,
              source: params.source,
              target: params.target,
              sourceHandle: params.sourceHandle,
              targetHandle: params.targetHandle,
              type: 'floating',
              data: {
                transitionType: params.sourceHandle as 'success' | 'error',
                actionName: '',
                labelKey: params.sourceHandle === 'success' ? 'Success' : 'Error',
                DisplayColor: params.sourceHandle === 'success' ? '#4caf50' : '#f44336',
                Icon: params.sourceHandle === 'success' ? 'mdi:check-circle' : 'mdi:alert-circle',
                AllowComments: false,
                scope: null,
                extraAttributes: null
              }
            };
            console.log('New edge:', newEdge);
            setEdges((edges) => [...edges, newEdge]);
            setNewTransitionsUuid((prevUuids) => [...prevUuids, newUuid]);
            setHasChanges(true);
          } else {
            console.log('Opening edge drawer for regular transition');
            // For regular transitions, open the drawer
            setEdgeSelected(params);
          }
        },
        [setEdges, setNewTransitionsUuid],
    );

    const clearIdsOfNewStatesAndTransitions = (data: any) => {
        const newData = { ...data };
        newData.states.forEach((state) => {
            if (isNewState(state.reactFlowId)) {
                state.id = 0;
            }
        })
        newData.transitions.forEach((transition) => {
            if (isNewTransition(transition.reactFlowId)) {
                transition.id = 0;
            }
        })

        return newData;

    }

    const findNode = useCallback((id: string) => nodes.find((node) => node.id === id), [nodes]);
    const pushVirtualInitialNode = (nodes: EditableNodeType[], edges: FloatingEdgeType[]) => {
        const initialNode = nodes.find((node) => node.data.initial);
        if (!initialNode) {
            return
        }
        const initialVirtualNode : EditableNodeType = {
            id: "initial-node",
            parentId: initialNode.id,
            type: 'editableNode',
            data: {
                internalId: "initial-node",
                name: 'Initial',
                labelKey: 'Initial',
                editScopes: "",
                viewScopes: "",
                deleteScopes: "",
                initial: true,
                final: false,
                transitionEditMode: false,
                color: null,
                extraAttributes: {
                    virtualInitialNode: true
                },
            },
            position: {
                x: 50,
                y: -100,
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        }
        const initialVirtualEdge : FloatingEdgeType = {
            id: "initial-edge",
            source: "initial-node",
            target: initialNode.id,
            selectable: false,
            type: 'floating',
            data: {
                actionName: 'Initial',
                labelKey: 'Initial',
                Icon: null,
                AllowComments: false,
                DisplayColor: null,
                extraAttributes: {
                    virtualInitialEdge: true
                },
                scope: null,
            },
        }
        nodes.push(initialVirtualNode);
        edges.push(initialVirtualEdge);
    }
    const pushVirtualFinalNode = (nodes: EditableNodeType[], edges: FloatingEdgeType[]) => {
        const finalNode = nodes.find((node) => node.data.final);
        if (!finalNode) {
            return
        }
        const finalVirtualNode : EditableNodeType = {
            id: "final-node",
            parentId: finalNode.id,
            type: 'editableNode',
            data: {
                internalId: "final-node",
                name: 'Final',
                labelKey: 'Final',
                editScopes: "",
                viewScopes: "",
                deleteScopes: "",
                initial: false,
                final: true,
                transitionEditMode: false,
                color: null,
                extraAttributes: {
                    virtualFinalNode: true
                },
            },
            position: {
                x: 50,
                y: 100,
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        }
        const finalVirtualEdge : FloatingEdgeType = {
            id: "final-edge",
            source: finalNode.id,
            target: "final-node",
            selectable: false,
            type: 'floating',
            data: {
                actionName: 'Final',
                labelKey: 'Final',
                Icon: null,
                AllowComments: false,
                DisplayColor: null,
                extraAttributes: {
                    virtualFinalEdge: true
                },
                scope: null,
            },
        }
        nodes.push(finalVirtualNode);
        edges.push(finalVirtualEdge);
    }

    const createRandomId = () : string =>  {
        let id : number = Math.floor(Math.random() * 1000000);
        //check if exist in node
        if(nodes.find((node) => node.id === id.toString())){
            return createRandomId();
        }
        //check if exist in edge
        if(edges.find((edge) => edge.id === id.toString())){
            return createRandomId();
        }
        return id.toString();
    }

    const addState = (stateName: string) => {
        if (stateName) {
            const newState: EditableNodeType = {
                id: createRandomId(),
                type: 'editableNode',
                data: {
                    internalId: null,
                    name: stateName,
                    labelKey: stateName,
                    editScopes: "",
                    viewScopes: "",
                    deleteScopes: "",
                    initial: false,
                    final: false,
                    extraAttributes: {},
                    transitionEditMode: false,
                    color: null,
                },
                position: { x: Math.random() * 400, y: Math.random() * 200 },
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
            }
            setNewStatesUuid([...newStatesUuid, newState.id]);
            saveNode(newState);
        }
    }

    useEffect(() => {
      setNodes((nds) => nds.map((node) => ({ ...node, data: { ...node.data, transitionEditMode: addTransitionMode } })))
    }, [addTransitionMode])

    const updateEdgesOnWorkflowData = (edges: FloatingEdgeType[]) => {
        setWorkflowData((oldData) => ({
            ...oldData,
            transitions: edges.filter((edge) => !edge.data.extraAttributes?.virtualInitialEdge && !edge.data.extraAttributes?.virtualFinalEdge).map((edge) => {
                const oldTransition = oldData.transitions.find((transition) => transition.id === parseInt(edge.id))
                const transition: WorkflowTransitionDTO = {
                    id: oldTransition?parseInt(edge.id):0,
                    isDeleted: oldTransition?.isDeleted||false,
                    idWorkflowStateFrom: edge.source?parseInt(edge.source):null,
                    idWorkflowStateTo: edge.target?parseInt(edge.target):null,
                    actionName: edge.data.actionName,
                    labelKey: edge.data.labelKey,
                    displayOrder: oldTransition?.displayOrder || 0,
                    displayColor: edge.data.DisplayColor,
                    icon: edge.data.Icon,
                    allowComments: edge.data.AllowComments,
                    extraAttributes: JSON.stringify(edge.data.extraAttributes),
                    idScope: oldTransition?.idScope || 0,
                    reactFlowId: edge.id,
                    stateFromReactFlowId: edge.source,
                    stateToReactFlowId: edge.target,
                }
                return transition
            })
        }))
    }

    const saveNode = (node: EditableNodeType) => {
        setLoading(true);
        setNodeSelected(null)

        node.data.extraAttributes = {
            ...node.data.extraAttributes,
            position:{
                x: node.position.x,
                y: node.position.y,
            }
        }
        
        let isNew = false;
        
        const newNodes = nodes.map((oldNode) => (oldNode.id === node.id ? {
            ...oldNode,
            ...node
        } : oldNode));

        if (!newNodes.some((n) => n.id === node.id)) {
            isNew = true;
            newNodes.push(node);
        }

        const workFlowDataToSave = {
            ...workflowData,
            states: newNodes.filter((node) => !node.data.extraAttributes?.virtualInitialNode && !node.data.extraAttributes?.virtualFinalNode).map((node) => {
                const oldNode = workflowData.states.find((state) => state.id === parseInt(node.id??'-1'))
                
                // For automatic action nodes, find success and failure edges
                let idWorkflowStateSuccess = null;
                let idWorkflowStateFailure = null;
                
                if (node.data.automaticAction) {
                    // Find edges coming out of this node
                    const outgoingEdges = edges.filter(edge => edge.source === node.id);
                    
                    outgoingEdges.forEach(edge => {
                        // Check edge handle to determine if it's success or error
                        if (edge.sourceHandle === 'success') {
                            idWorkflowStateSuccess = edge.target ? parseInt(edge.target) : null;
                        } else if (edge.sourceHandle === 'error') {
                            idWorkflowStateFailure = edge.target ? parseInt(edge.target) : null;
                        }
                    });
                }
                
                const state: WorkflowStateDTO = {
                    id: isNew?0:parseInt(node.id),
                    isDeleted: false,
                    name: node.data.name,
                    idWorkflow: workflowData.id,
                    complete: node.data.final?true:false,
                    extraAttributes: JSON.stringify(node.data.extraAttributes),
                    labelKey: node.data.labelKey,
                    action: node.data.name,
                    reactFlowId: node.id,
                    viewScopes: node.data.viewScopes,
                    editScopes: node.data.editScopes,
                    deleteScopes: node.data.deleteScopes,
                    initial: node.data.initial?true:false,
                    editable: oldNode?.editable || false,
                    deletable: oldNode?.deletable || false,
                    viewable: oldNode?.viewable || false,
                    editableByOwner: oldNode?.editableByOwner || false,
                    deletableByOwner: oldNode?.deletableByOwner || false,
                    viewableByOwner: oldNode?.viewableByOwner || false,
                    displayColor: node.data.color,
                    idStateType: node.data.automaticAction?.stateType ? STATE_TYPE_TO_ID[node.data.automaticAction.stateType as AutomaticStateType] : null,
                    idWorkflowStateSuccess,
                    idWorkflowStateFailure,
                    parameterConfiguration: node.data.automaticAction?.config ? JSON.stringify(node.data.automaticAction.config) : null
                }
                return state
            }),
            transitions: edges.filter((edge) => {
                // Filter out virtual edges
                if (edge.data.extraAttributes?.virtualInitialEdge || edge.data.extraAttributes?.virtualFinalEdge) {
                    return false;
                }
                
                // Filter out edges connected to automatic action nodes
                const sourceNode = newNodes.find(n => n.id === edge.source);
                if (sourceNode?.data?.automaticAction) {
                    return false; // These are handled via idWorkflowStateSuccess/Failure
                }
                
                return true;
            }).map((edge) => {
                const isNew = isNewTransition(edge.id);
                const oldTransition = workflowData.transitions.find((transition) => transition.id === parseInt(edge.id))
                const transition: WorkflowTransitionDTO = {
                    id: isNew?0:parseInt(edge.id),
                    isDeleted: oldTransition?.isDeleted || false,
                    idWorkflowStateFrom: edge.source?parseInt(edge.source):null,
                    idWorkflowStateTo: edge.target?parseInt(edge.target):null,
                    actionName: edge.data.actionName,
                    labelKey: edge.data.labelKey,
                    displayOrder: oldTransition?.displayOrder || 0,
                    displayColor: edge.data.DisplayColor,
                    icon: edge.data.Icon,
                    allowComments: edge.data.AllowComments,
                    extraAttributes: null,
                    idScope: oldTransition?.idScope || 0,
                    reactFlowId: edge.id,
                    stateFromReactFlowId: edge.source,
                    stateToReactFlowId: edge.target,
                }
                return transition
            })
        };

        saveWorkflowState(workFlowDataToSave).then((response) => {
            //updateNodesOnWorkflowData(newNodes);
            setNodes(newNodes)
        }).finally(() => {
            setLoading(false)
        })
    }

    const removeNode = (id: string) => {
        // Immediately update UI optimistically
        setNodes((oldNodes) => oldNodes.filter((node) => node.id !== id));
        setEdges((oldEdges) => oldEdges.filter((edge) => edge.source !== id && edge.target !== id));
        setNodeSelected(null);
        setNewStatesUuid(newStatesUuid.filter((uuid) => uuid !== id));
        
        // Update workflow data state
        saveWorkflowState({
            ...workflowData,
            states: workflowData.states.filter((state) => {
                // Remove if matches by id (persisted) or by reactFlowId (new)
                return state.id !== parseInt(id) && state.reactFlowId !== id;
            }),
            transitions: workflowData.transitions.filter((transition) => {
                // Filter by both numeric id and string reactFlowId
                return transition.idWorkflowStateFrom !== parseInt(id) && 
                       transition.idWorkflowStateTo !== parseInt(id) &&
                       transition.stateFromReactFlowId !== id &&
                       transition.stateToReactFlowId !== id;
            })
        });
    }

    const removeEdge = (id: string) => {
        saveWorkflowState({
            ...workflowData,
            transitions: workflowData.transitions.filter((transition) => transition.id !== parseInt(id))
        }).then((response) => {
            setEdges((oldEdges) => oldEdges.filter((edge) => edge.id !== id));
            setEdgeSelected(null);
        })
        setNewTransitionsUuid(newTransitionsUuid.filter((uuid) => uuid !== id));
    }

    const saveEdge = (edge: FloatingEdgeType) => {
        setLoading(true);
        setEdgeSelected(null);
        setAddTransitionMode(false);

        const newEdges = edges.map((oldEdge) => (oldEdge.id === edge.id ? {
            ...oldEdge,
            ...edge
        } : oldEdge));


        if(!edge?.id){
            const newUuid = createRandomId();
            newEdges.push({
                ...edge,
                id: newUuid
            });
            setNewTransitionsUuid([...newTransitionsUuid, newUuid]);
        }

        const workFlowDataToSave = {
            ...workflowData,
            states: nodes.filter((node) => !node.data.extraAttributes?.virtualInitialNode && !node.data.extraAttributes?.virtualFinalNode).map((node) => {
                const oldNode = workflowData.states.find((state) => state.id === parseInt(node.id))
                const state: WorkflowStateDTO = {
                    id: oldNode?parseInt(node.id):0,
                    isDeleted: false,
                    name: node.data.name,
                    idWorkflow: workflowData.id,
                    complete: node.data.final?true:false,
                    extraAttributes: JSON.stringify(node.data.extraAttributes),
                    labelKey: node.data.labelKey,
                    action: node.data.name,
                    reactFlowId: node.id,
                    viewScopes: node.data.viewScopes,
                    editScopes: node.data.editScopes,
                    deleteScopes: node.data.deleteScopes,
                    initial: node.data.initial?true:false,
                    editable: oldNode?.editable || false,
                    deletable: oldNode?.deletable || false,
                    viewable: oldNode?.viewable || false,
                    editableByOwner: oldNode?.editableByOwner || false,
                    deletableByOwner: oldNode?.deletableByOwner || false,
                    viewableByOwner: oldNode?.viewableByOwner || false,
                    displayColor: node.data.color
                }
                return state
            }),
            transitions: newEdges.filter((edge) => !edge.data.extraAttributes?.virtualInitialEdge && !edge.data.extraAttributes?.virtualFinalEdge).map((edge) => {
                const oldTransition = workflowData.transitions.find((transition) => transition.id === parseInt(edge.id))
                const transition: WorkflowTransitionDTO = {
                    id: oldTransition?parseInt(edge.id):0,
                    isDeleted: oldTransition?.isDeleted||false,
                    idWorkflowStateFrom: parseInt(edge.source),
                    idWorkflowStateTo: parseInt(edge.target),
                    actionName: edge.data.actionName,
                    labelKey: edge.data.labelKey,
                    displayOrder: oldTransition?.displayOrder,
                    displayColor: edge.data.DisplayColor,
                    icon: edge.data.Icon,
                    allowComments: edge.data.AllowComments,
                    extraAttributes: null,
                    idScope: edge.data?.scope||null,
                    reactFlowId: edge.id,
                    stateFromReactFlowId: edge.source,
                    stateToReactFlowId: edge.target,
                }
                return transition
            })
        };

        saveWorkflowState(workFlowDataToSave).then((response) => {
            updateEdgesOnWorkflowData(newEdges);
            setEdgeSelected(null)
            setEdges(newEdges)
        }).finally(() => {
            setLoading(false)
        })
    }

    const removeNodeConfirm = (id: string) => {
        setItemToDelete({ type: 'node', id });
        setDeleteConfirmOpen(true);
    }

    const removeEdgeConfirm = (id: string) => {
        setItemToDelete({ type: 'edge', id });
        setDeleteConfirmOpen(true);
    }

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            if (itemToDelete.type === 'node') {
                removeNode(itemToDelete.id);
            } else {
                removeEdge(itemToDelete.id);
            }
        }
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
    }

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
    }

    const layoutContext = useLayout();
    useEffect(() => {
        layoutContext.setLoading(loading);
    }, [loading])


    // Toolbox visibility state
    const [showToolbox, setShowToolbox] = useState(false);
    // Toolbox position state (default: top-right)
    const [toolboxPos, setToolboxPos] = useState({ x: window.innerWidth - 360, y: 32 });
    const [dragging, setDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Mouse event handlers for dragging the toolbox
    const handleToolboxMouseDown = (e: React.MouseEvent) => {
        setDragging(true);
        setDragOffset({
            x: e.clientX - toolboxPos.x,
            y: e.clientY - toolboxPos.y
        });
        // Prevent text selection
        document.body.style.userSelect = 'none';
    };

    useEffect(() => {
        if (!dragging) return;
        const handleMouseMove = (e: MouseEvent) => {
            setToolboxPos(pos => ({
                x: Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - 320)),
                y: Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - 48))
            }));
        };
        const handleMouseUp = () => {
            setDragging(false);
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging, dragOffset]);

    // Handle drag over for React Flow canvas
    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    // Handle drop from toolbox to canvas
    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        const type = event.dataTransfer.getData('application/reactflow');
        if (!type) return;

        // Convert screen coordinates to flow coordinates accounting for zoom/pan
        const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY
        });

        // Create a new node for the dropped type
        const newNode: EditableNodeType = {
            id: createRandomId(),
            type: 'editableNode',
            data: {
                internalId: null,
                name: type,
                labelKey: type,
                editScopes: '',
                viewScopes: '',
                deleteScopes: '',
                initial: false,
                final: false,
                extraAttributes: { automatic: true },
                transitionEditMode: false,
                color: null,
                automaticAction: {
                    stateType: type,
                    config: {
                        parameters: [],
                        retryConfig: {
                            maxRetries: 3,
                            retryDelay: 1000
                        }
                    }
                }
            },
            position,
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        };
        setNewStatesUuid([...newStatesUuid, newNode.id]);
        saveNode(newNode);
        setTimeout(() => {
            setNodeSelected(newNode);
        }, 0);
    };


    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', height: '100vh' }}>
            {/* Movable Toolbox as a floating panel */}
            {showToolbox && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: toolboxPos.x,
                        top: toolboxPos.y,
                        zIndex: 2000,
                        width: 320,
                        cursor: dragging ? 'grabbing' : 'default',
                        boxShadow: 6
                    }}
                >
                    <Paper elevation={3} sx={{ width: '100%', maxHeight: '80vh', overflow: 'auto', p: 0 }}>
                        {/* Drag handle area */}
                        <Box
                            sx={{
                                width: '100%',
                                height: 32,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                cursor: 'grab',
                                borderTopLeftRadius: 1,
                                borderTopRightRadius: 1,
                                userSelect: 'none',
                            }}
                            onMouseDown={handleToolboxMouseDown}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {intl.get('workflowEditor.automaticActionsToolbox')}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowToolbox(false);
                                }}
                                sx={{
                                    color: 'inherit',
                                    padding: 0.5,
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box>
                            <AutomaticActionToolbox
                                onStateTypeSelect={(stateType) => {
                                    // TODO: Add logic to insert a new automatic action node of the selected type
                                    console.log('Selected automatic state type:', stateType);
                                }}
                            />
                        </Box>
                    </Paper>
                </Box>
            )}
            {/* Main workflow editor content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card sx={{m:2}}>
                    <Box sx={{m:2}}>
                        {/* Show ParameterConfiguration for automatic action nodes, EditableNodeDrawer for regular nodes */}
                        {(() => {
                            const hasAutomaticAction = nodeSelected?.data?.automaticAction?.stateType;
                            console.log('Render check:', {
                                nodeSelected: !!nodeSelected,
                                hasData: !!nodeSelected?.data,
                                hasAutomaticAction: !!nodeSelected?.data?.automaticAction,
                                hasStateType: hasAutomaticAction,
                                stateType: nodeSelected?.data?.automaticAction?.stateType,
                                fullAutomaticAction: nodeSelected?.data?.automaticAction
                            });
                            return hasAutomaticAction;
                        })() ? (
                            <ParameterConfiguration
                                currentNodeId={nodeSelected.id}
                                moduleId={workflowData?.idModule}
                                stateType={nodeSelected.data.automaticAction.stateType as any}
                                config={nodeSelected.data.automaticAction.config || { parameters: [], retryConfig: { maxRetries: 3, retryDelay: 1000 } }}
                                workflowContext={{
                                    moduleData: { moduleName: 'timeoff', fields: [] },
                                    previousOutputs: {},
                                    userInfo: { id: '', name: '', email: '', roles: [] }
                                }}
                                onChange={(config) => {
                                    const updatedNode = {
                                        ...nodeSelected,
                                        data: {
                                            ...nodeSelected.data,
                                            automaticAction: {
                                                ...nodeSelected.data.automaticAction,
                                                config
                                            }
                                        }
                                    };
                                    saveNode(updatedNode);
                                }}
                                onClose={() => setNodeSelected(null)}
                            />
                        ) : (
                            <EditableNodeDrawer 
                                selectedNode={nodeSelected} 
                                onClose={() => setNodeSelected(null)} 
                                saveNode={saveNode} 
                                deleteNode={(id) => removeNodeConfirm(id)} 
                            />
                        )}
                        <FloatingEdgeDrawer selectedEdge={edgeSelected} onClose={() => setEdgeSelected(null)} saveEdge={saveEdge} deleteEdge={(id) => removeEdgeConfirm(id)} />
                        <Stack gap={2} marginY={2}>
                           
                            {id ?
                                <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>{intl.get('workflowDetail.header')}</Typography>
                                :
                                <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>{intl.get('newWorkflow.header')}</Typography>
                            }
                            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                                <Box display={"flex"} gap={2}>
                                    <AddStateModal
                                        open={isModalOpen}
                                        setOpen={setIsModalOpen}
                                        onAddState={addState}
                                    />
                                    <Button 
                                        size='small' 
                                        variant='outlined' 
                                        color='primary' 
                                        startIcon={<AutomaticActionsIcon />}
                                        onClick={() => setShowToolbox(!showToolbox)}
                                    >
                                        {intl.get('workflowEditor.automaticActions')}
                                    </Button>
                                    <PermissionsGate  viewScopes={[SCOPES['workflows.edit']]} editScopes={[SCOPES['workflows.edit']]} editRoles={[]} viewRoles={[]}>
                                        <Button size='small' disabled={!hasChanges}  variant='contained' color='primary' className='newAddress' onClick={() => saveAction(workflowData)}>
                                            {intl.get('save.button')}
                                        </Button>
                                    </PermissionsGate>
                                </Box>
                                <Button startIcon={<ArrowBack />} size='small' variant='contained' color='primary' className='newAddress' onClick={() => navigateWithConfirmation(`/workflows`)}>
                                    {intl.get('workflowEditor.button.backToWorkflows')}
                                </Button>
                            </Box>
                            <Stack direction="row" spacing={2} sx={{ width: 'auto', maxWidth: '600px' }}>
                                <FormControl size='small' sx={{ minWidth: '300px' }}>
                                    <TextField
                                        size='small'
                                        id="workflow-name"
                                        label={intl.get('workflow.name')}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: workflowData?.name ? true : false,
                                            }
                                        }}
                                        value={workflowData?.name || ''}
                                        onChange={(e) => {
                                            setWorkflowData((oldData) => ({ ...oldData, name: e.target.value }))
                                            setHasChanges(true);
                                        }} />
                                </FormControl>
                                <FormControl size='small' sx={{ minWidth: '250px' }} disabled={id !== 'new'}>
                                    <InputLabel shrink>{intl.get('workflow.module')}</InputLabel>
                                    <Select
                                        notched
                                        label={intl.get('workflow.module')}
                                        value={workflowData?.idModule || 1}
                                        onChange={(e) => {
                                            setWorkflowData((oldData) => ({ ...oldData, idModule: e.target.value as number }))
                                            setHasChanges(true);
                                        }}
                                    >
                                        <MenuItem value={WORKFLOW_MODULES.COMPLETE_WORKFLOW}>
                                            {intl.get('workflow.modules.completeWorkflow')}
                                        </MenuItem>
                                        <MenuItem value={WORKFLOW_MODULES.TIME_OFF}>
                                            {intl.get('workflow.modules.timeOff')}
                                        </MenuItem>
                                        <MenuItem value={WORKFLOW_MODULES.INVOICING}>
                                            {intl.get('workflow.modules.invoicing')}
                                        </MenuItem>
                                        <MenuItem value={WORKFLOW_MODULES.ACCOUNTS_PAYABLE}>
                                            {intl.get('workflow.modules.accountsPayable')}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                        <div style={{ height: '70vh', width: '100%', overflow: 'hidden' }} className="floatingedges">
                            <ReactFlow
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={(nodes) => {onNodesChange(nodes)}}
                                onEdgesChange={(edges) => {onEdgesChange(edges)}}
                                onConnect={onConnect}
                                fitView
                                connectionLineComponent={FloatingConnectionLine}
                                nodeTypes={nodeTypes}
                                edgeTypes={edgeTypes}
                                onNodeClick={(event, node) => {
                                    console.log('Node clicked:', {
                                        id: node.id,
                                        hasAutomaticAction: !!node.data?.automaticAction,
                                        automaticAction: node.data?.automaticAction,
                                        stateType: node.data?.automaticAction?.stateType
                                    });
                                    setNodeSelected(node)
                                }}
                                onEdgeClick={(event: React.MouseEvent, edge: FloatingEdgeType) => {
                                    setEdgeSelected(edge)
                                }}
                                defaultViewport={defaultViewport}
                                onNodeDragStop={(event, node) => {
                                    saveNode(node)
                                }}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
   
                                <Panel>
                                    <Paper sx={{width: '120px'}} variant='outlined'>
                                        <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} gap={1} p={1}>
                                            <ToggleButtonGroup
                                                color="primary"
                                                value={addTransitionMode}
                                                exclusive
                                                onChange={(e, newValue) => setAddTransitionMode(newValue?true:false)}
                                                aria-label="text alignment"
                                            >
                                                <ToggleButton value={false}>
                                                    <FlipToFront />
                                                </ToggleButton>
                                                <ToggleButton value={true}>
                                                    <TurnSharpRight/>
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            <Typography sx={{fontSize: '.8rem', fontWeight: 'bold'}}>
                                            {addTransitionMode?intl.get('workflowDetail.button.editTransitions'):intl.get('workflowDetail.button.dragAndDrop')}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Panel>
                                <Controls />
                                <Background />
                                <MiniMap nodeColor={() => 'gray'} nodeStrokeWidth={3} zoomable pannable maskStrokeColor='black' />
                            </ReactFlow>
                        </div>
                        <Box p={2}>
                            <Box display={'flex'} flexDirection='row' alignItems='center' gap={1}>
                                <MultipleStop />
                                <Typography variant='h4'>{intl.get('workflowEditor.workflowTransitions')}</Typography>
                            </Box>
                            <List>
                                {edges.filter((edge) => !edge.data.extraAttributes?.virtualInitialEdge && !edge.data.extraAttributes?.virtualFinalEdge).map((edge) => {
                                    const nodeSource = findNode(edge.source);
                                    const nodeTarget = findNode(edge.target);

                                    return(<Fragment key={`${edge.id}-transition-item`}>
                                        <ListItem className='border-bottom border-gray-200 m-0'>
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                                                        {edge?.data?.Icon && (
                                                            <MuiIconDisplay
                                                                iconName={edge.data.Icon}
                                                                sx={{ 
                                                                    fontSize: 28,
                                                                    color: edge?.data?.DisplayColor || 'inherit'
                                                                }}
                                                            />
                                                        )}
                                                        <Stack style={{width:"250px"}}>
                                                            <Typography p={0} className='text-sm' style={{textOverflow: "ellipsis"}}>{`#${edge?.id} - ${edge.data?.labelKey&&intl.get(edge.data?.labelKey)|| edge?.data?.actionName}`}</Typography>
                                                            <Typography p={0} className='text-sm' style={{textOverflow: "ellipsis"}}>{intl.get('workflowEditor.name')}:{edge?.data?.actionName}</Typography>
                                                            <Typography p={0} className='text-sm' style={{textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{intl.get('workflowEditor.labelKey')}:{edge?.data?.labelKey}</Typography>
                                                        </Stack>
                                                        <Divider orientation="vertical" flexItem style={{backgroundColor: 'black'}} />
                                                        <>
                                                            {nodeSource ? <Chip size='small' label={`#${nodeSource?.id} - ${nodeSource.data?.name}`} className='border border-gray' /> :
                                                            <Chip size='small' variant='outlined' label={intl.get('workflowEditor.noSource')} className='border border-grey  w-68' />}
                                                            <SwipeRightAlt />
                                                            {nodeTarget ? <Chip size='small' label={`#${nodeTarget?.id} - ${nodeTarget.data?.name}`} className='border border-gray' /> :
                                                            <Chip size='small' variant='outlined' label={intl.get('workflowEditor.noTarget')} className='border border-grey' />}
                                                        </>
                                                    </Box>
                                                }
                                            />
                                            <Box>
                                                <IconButton onClick={() => setEdgeSelected(edge)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton onClick={() => removeEdgeConfirm(edge.id)}>
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        <Divider className='w-100 border-solid border-gray-200' />
                                    </Fragment>)
                                })}
                            </List>
                        </Box>           
                    </Box>
                </Card>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {intl.get('delete.modal.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {intl.get('confirm.modal.workflowDetail.message')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        {intl.get('delete.modal.cancel.button')}
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                        {intl.get('delete.modal.delete.button')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Navigation Blocker Dialog */}
            <Dialog
                open={showNavigationDialog}
                onClose={handleNavigationCancel}
                aria-labelledby="unsaved-changes-dialog-title"
                aria-describedby="unsaved-changes-dialog-description"
            >
                <DialogTitle id="unsaved-changes-dialog-title">
                    {intl.get('workflowEditor.unsavedChanges.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="unsaved-changes-dialog-description">
                        {intl.get('workflowEditor.unsavedChanges.message')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleNavigationCancel} color="primary">
                        {intl.get('workflowEditor.unsavedChanges.stay')}
                    </Button>
                    <Button 
                        onClick={handleNavigationConfirm} 
                        color="error" 
                        variant="contained" 
                        autoFocus
                    >
                        {intl.get('workflowEditor.unsavedChanges.leave')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

const FlowWithProvider: React.FC = (props) => {
    return (
        <ReactFlowProvider>
            <WorkflowEditor {...props} />
        </ReactFlowProvider>
    )
}

export default FlowWithProvider
